import * as React from "react"

import { cn } from "../lib/utils"

interface CheckboxProps extends Omit<React.ComponentProps<"input">, "type"> {
  /** Visible label rendered beside the box and wired up via htmlFor. */
  label?: React.ReactNode
  /** Secondary copy under the label, exposed as the input's description. */
  description?: React.ReactNode
  /** Renders the tri-state "partially selected" bar (select-all headers). */
  indeterminate?: boolean
}

/**
 * Native `<input type="checkbox">` with the DS treatment.
 *
 * Deliberately not a Radix primitive: every call site in the app is a plain
 * checkbox driven by `checked`/`onChange`, and Radix renders a
 * `<button role="checkbox">` with a hidden input, which changes form semantics
 * and event shape. Wrapping the native control keeps this a drop-in.
 */
const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, label, description, indeterminate, id, ...props }, ref) => {
    const generatedId = React.useId()
    const inputId = id || generatedId
    const descriptionId = description ? `${inputId}-description` : undefined

    const innerRef = React.useRef<HTMLInputElement | null>(null)
    React.useImperativeHandle(ref, () => innerRef.current as HTMLInputElement)

    // `indeterminate` is a DOM property with no HTML attribute equivalent.
    React.useEffect(() => {
      if (innerRef.current) {
        innerRef.current.indeterminate = Boolean(indeterminate)
      }
    }, [indeterminate])

    const input = (
      <input
        type="checkbox"
        id={inputId}
        ref={innerRef}
        aria-checked={indeterminate ? "mixed" : undefined}
        aria-describedby={descriptionId}
        className={cn(
          "h-4 w-4 rounded border-neutral-300 text-primary-600 shadow-sm",
          "focus:ring-2 focus:ring-primary-500 focus:ring-offset-0",
          "disabled:cursor-not-allowed disabled:opacity-50",
          className
        )}
        {...props}
      />
    )

    if (!label && !description) {
      return input
    }

    return (
      <div className="flex items-start gap-2">
        {input}
        <div className="grid gap-0.5 leading-none">
          {label && (
            <label
              htmlFor={inputId}
              className={cn(
                "text-sm font-medium text-neutral-700 select-none",
                props.disabled && "cursor-not-allowed opacity-50"
              )}
            >
              {label}
            </label>
          )}
          {description && (
            <p id={descriptionId} className="text-sm text-muted-foreground">
              {description}
            </p>
          )}
        </div>
      </div>
    )
  }
)
Checkbox.displayName = "Checkbox"

export { Checkbox }
export type { CheckboxProps }
